import { SEARCH_DATA_KEY } from 'api/search'
import { QueryKey, useQueryClient } from '@tanstack/react-query'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useUpdateFields } from 'api/aws/organizations_table/useUpdateFields'
import { normalizeTeam } from 'utils/normalizeTeam'
import type { Organization } from 'types'

type UpdateOpts = {
  usersIndex?: Map<string, any> // for id→user expansion when needed
  orgQueryKeys?: Array<QueryKey> // optional extra per-org keys to patch
}

export const useUpdateOrganizationField = (org?: Pick<Organization, 'PK' | 'SK' | 'org_uuid'>) => {
  const qc = useQueryClient()
  const { syncOrganizationFields } = useSyncQueryCache()
  const { mutateAsync: updateFields } = useUpdateFields()

  return async (fieldName: string, newValue: any, opts?: UpdateOpts) => {
    if (!org) return
    const allKey = SEARCH_DATA_KEY
    const prevAll = qc.getQueryData<any>(allKey)

    // Prepare value (expand team_details to user objects)
    const payloadValue =
      fieldName === 'team_details' ? normalizeTeam(Array.isArray(newValue) ? newValue : [], opts?.usersIndex) : newValue

    // Optimistic: patch organizations in allItems
    qc.setQueryData(allKey, (all: any) => {
      if (!all || !Array.isArray(all.organizations)) return all
      const organizations = all.organizations.map((o: any) =>
        o.PK === org.PK && o.SK === org.SK ? { ...o, [fieldName]: payloadValue } : o
      )
      return { ...all, organizations }
    })

    try {
      // Persist
      await updateFields({
        updates: [
          {
            tableName: 'ORGANIZATIONS_TABLE',
            PK: org.PK,
            SK: org.SK,
            fieldName,
            newValue: payloadValue,
          },
        ],
      })

      // Sync big cache (field-level)
      syncOrganizationFields(qc, org as any, [{ fieldName, newValue: payloadValue }])

      // Patch common per-org caches (adjust to your keys)
      qc.setQueryData(['organization', org.org_uuid], (prev: any) =>
        prev ? { ...prev, [fieldName]: payloadValue } : prev
      )
      qc.setQueryData(['organizationById', org.org_uuid], (prev: any) =>
        prev ? { ...prev, [fieldName]: payloadValue } : prev
      )

      // Patch any extra keys the caller cares about
      opts?.orgQueryKeys?.forEach(key => {
        qc.setQueryData(key, (prev: any) => (prev ? { ...prev, [fieldName]: payloadValue } : prev))
      })
    } catch (e) {
      // rollback
      qc.setQueryData(allKey, prevAll)
      throw e
    }
  }
}
